// Phase 83p — Branding config (logos + colors + typography + SEO) hook.
// Cached 5 minutes. Components that consume branding-driven values should
// import this hook + spread the relevant slice into their props/styles.
import { useQuery } from "@tanstack/react-query";
import { edgeGet } from "../lib/edgeApi";

const STATIC_FALLBACK = {
  logos: { mark: null, full: null, wordmark: null, favicon: null },
  typography: {
    // Sprint 2 (02-2026) — Inter làm mặc định (bundle 2 weight critical).
    primary_font: "Inter",
    heading_font: "Inter",
    google_font_url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap",
  },
  colors: {
    court: {
      bg_stop_1: "#1F3A66",
      bg_stop_2: "#16294C",
      bg_stop_3: "#0B1A33",
      court_fill: "rgba(70,118,178,0.35)",
      kitchen_fill: "rgba(120,158,210,0.22)",
      line: "rgba(248,250,252,0.92)",
      net: "rgba(248,250,252,0.98)",
      preset: "pro_blue",
      bg_image_url: null,
    },
    bracket: {
      winner: "#10B981",
      path_highlight: "#3B82F6",
      warn_same_group: "#F59E0B",
      error_same_group: "#F43F5E",
      final_winner_grad_a: "#FBBF24",
      final_winner_grad_b: "#FCD34D",
      preset: "default",
    },
    accent: { orange: "#FF8A00", lime: "#A8FF00" },
  },
};

// v17.03 — Last-known cache trong localStorage: khi cả edge lẫn origin fail
// (mạng chết, worker 503…) UI vẫn có branding bản mới nhất người dùng từng
// nhận được → app không bao giờ chớp trắng logo/màu.
const LKS_KEY = "branding_last_known_v1";
function _readLastKnown() {
  try {
    const raw = localStorage.getItem(LKS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch { /* corrupt storage */ }
  return null;
}
function _writeLastKnown(data) {
  try { localStorage.setItem(LKS_KEY, JSON.stringify(data)); } catch { /* quota */ }
}

async function _fetchBranding() {
  try {
    const r = await edgeGet("/branding", { policyKey: "branding" });
    if (r?.data) _writeLastKnown(r.data);
    return r.data;
  } catch {
    // Edge + origin đều fail → last-known → default. KHÔNG throw để React
    // Query giữ trạng thái ổn định, tránh loop retry.
    const lk = _readLastKnown();
    return lk || STATIC_FALLBACK;
  }
}

export function useBranding() {
  const q = useQuery({
    queryKey: ["branding"],
    queryFn: _fetchBranding,
    staleTime: 5 * 60_000,
    retry: 0,
    // Trả last-known ngay khi mount (initialData) để KHÔNG chớp mặc định
    // trước khi network xong. React Query vẫn refetch để cập nhật.
    initialData: () => _readLastKnown() || undefined,
  });
  return q.data || STATIC_FALLBACK;
}

export function useCourtColors() {
  const b = useBranding();
  return b.colors?.court || STATIC_FALLBACK.colors.court;
}

export function useBracketColors() {
  const b = useBranding();
  return b.colors?.bracket || STATIC_FALLBACK.colors.bracket;
}
